import React, { useRef, useState, useEffect, useContext } from "react";
import { filterBy, process, orderBy } from "@progress/kendo-data-query";
import { Grid, GridColumn, GridToolbar } from "@progress/kendo-react-grid";
import axios from "axios";
import { BASE_URL, movementStatuses, CargoCustomsStatus } from "../../constants";
import { ExcelExport, ExcelExportColumn } from "@progress/kendo-react-excel-export";
import { formatDate } from "@telerik/kendo-intl";
import { ComboBox, MultiSelect } from "@progress/kendo-react-dropdowns";
import AppContext from "../../context/app-context";
import "../../index.scss";
import { Checkbox } from "@progress/kendo-react-inputs";
import KendoLoader from "../KendoLoader";
import { set } from "lodash";
import ReactModal from "react-modal";
import ScheduleForm from "./ScheduleForm";
import { AVAILABLE_COLUMNS } from "./EditableGrid";
import { ToastContainer } from "react-toastify";
 
const KendoDateCell = (props) => {
  const value = props.dataItem[props.field];
  if (value) return <td>{formatDate(new Date(value), "dd-MMM-yyyy")}</td>;
  else return <td>--</td>;
};

const KendoDateTimeCell = (props) => {
  const value = props.dataItem[props.field];
  if (value) return <td>{formatDate(new Date(value), "dd-MMM-yyyy HH:mm")}</td>;
  else return <td>--</td>;
};

const KendoCustomsCell = (props) => {
  
  // Check both cargoCustomsStatusCode (string) and cargoCustomsStatus (numeric)
  const statusCode = props.dataItem.cargoCustomsStatusCode;
  const statusId = props.dataItem.cargoCustomsStatus;
  
  // If we have the string code, find by code first
  if (statusCode) {
    const customsStatus = CargoCustomsStatus.find(status => status.code === statusCode);
    if (customsStatus) {
      return <td>{customsStatus.name}</td>;
    }
  }
  
  // If we have the numeric ID, find by ID
  if (statusId) {
    const customsStatus = CargoCustomsStatus.find(status => status.id === statusId);
    if (customsStatus) {
      return <td>{customsStatus.name}</td>;
    }
  }
  
  // Show empty when customs status is not available
  return <td></td>;
};

const KendoCurrencyCell = (props) => {
  const value = props.dataItem.totalValueDisplay;
  return <td>{value || ""}</td>;
};
 
const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    width: "98vw",
    maxWidth: "1700px",
    minWidth: "1000px",
    height: "96vh",
    maxHeight: "96vh",
    transform: "translate(-50%, -50%)",
    overflow: "hidden",
    padding: "0",
  },
};
 
const gridColumns = [
  {
    field: "vesselName",
    title: "Vessel",
    width: 200,
  },
  {
    field: "poNo",
    title: "Po No",
    filterable: false,
    width: 200,
  },
  {
    field: "commodity",
    title: "Commodity",
    filterable: false,
    width: 200,
  },
  {
    field: "supplierName",
    title: "Supplier",
    filterable: false,
    width: 200,
  },
  {
    field: "colli",
    title: "Qty",
    filterable: false,
    width: 100,
  },
  {
    field: "weight",
    title: "Weight(KG)",
    filterable: false,
    width: 140,
  },
  {
    field: "length",
    title: "Length(CM)",
    filterable: false,
    width: 140,
  },
  {
    field: "width",
    title: "Width(CM)",
    filterable: false,
    width: 140,
  },
  {
    field: "height",
    title: "Height(CM)",
    filterable: false,
    width: 140,
  },
  {
    field: "chargableWeight",
    title: "Ch. Wt.(KG)",
    filterable: false,
    width: 140,
  },
  {
    field: "volume",
    title: "Volume(M3)",
    filterable: false,
    width: 140,
  },
  {
    field: "cargoType",
    title: "Cargo Type",
    filterable: false,
    width: 140,
  },
  
  {
    field: "currentWarehouse",
    title: "Warehouse",
    filterable: false,
    width: 150,
  },
  {
    field: "location",
    title: "Location",
    filterable: false,
    width: 150,
  },
  {
    field: "movementStatus",
    title: "Status",
    filterable: true,
    width: 150,
  },
  {
    field: "movementStatusUpdatedDate",
    title: "Status Updated Date",
    cell: KendoDateTimeCell,
    filterable: true,
    width: 200,
  },
  {
    field: "receivedDate",
    title: "Date",
    cell: KendoDateCell,
    filterable: false,
    width: 150,
  },
  {
    field: "age",
    title: "Age (Days)",
    filterable: false,
    width: 150,
  },
  {
    field: "shipmentRefNo",
    title: "AWB/BL",
    filterable: false,
    width: 150,
  },
  {
    field: "remarks",
    title: "Ref",
    filterable: false,
    width: 200,
  },
  {
    field: "cargoCustomsStatusCode",
    title: "Customs Status",
    filterable: false,
    width: 160,
    cell: KendoCustomsCell,
  },
  {
    field: "cargoPickupType",
    title: "Shipment Type",
    filterable: false,
    width: 200,
  },
  {
    field: "totalValueDisplay",
    title: "Total Value",
    filterable: false,
    width: 180,
    cell: KendoCurrencyCell,
  },
];
 
export default function StockReport() {
  const authCtx = useContext(AppContext);
  const isAgent = authCtx.profile.role === 300;
  const agentVisibleMovementStatuses = [400, 500, 200];
  const [vessels, setVessels] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [vessel, setVessel] = useState(null);
  const [location, setLocation] = useState("");
  const [status, setStatus] = useState(() => {
    if (isAgent) {
      return movementStatuses.filter((x) =>
        agentVisibleMovementStatuses.includes(x.id)
      );
    }

    // Default for non-agent users: show both "Created" and "InWarehouse" statuses
    return movementStatuses.filter((x) => x.id === 150 || x.id === 200);
  });
  const [canceledPos, setCanceledPos] = useState(false);
  //const defaultState = { skip: 0, take: 10 };
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [schedulerOpen, setSchedulerOpen] = useState(false);
  //const [pageState, setPageState] = useState(defaultState);
 
  const exportColumnOptions = AVAILABLE_COLUMNS.map((column) => ({
    field: column.key,
    title: column.label,
  }));
  const [exportPickerOpen, setExportPickerOpen] = useState(false);
  const [selectedExportColumns, setSelectedExportColumns] = useState(
    exportColumnOptions.map((column) => column.field)
  );
  const exporterRef = useRef(null);

  const excelExport = () => {
    setExportPickerOpen(true);
  };

  const toggleExportColumn = (field) => {
    setSelectedExportColumns((prev) =>
      prev.includes(field) ? prev.filter((item) => item !== field) : [...prev, field]
    );
  };

  const downloadExport = () => {
    save(exporterRef.current);
    setExportPickerOpen(false);
  };

  const save = (component) => {
    const options = component.workbookOptions();
    const sheet = options.sheets[0];
    const rows = sheet.rows;
    const headerRow = rows.find((r) => r.type === "header");
    if (!headerRow) {
      exporterRef.current?.save(options);
      return;
    }

    const selectedFields =
      selectedExportColumns.length > 0
        ? selectedExportColumns
        : exportColumnOptions.map((column) => column.field);
    const selectedTitles = exportColumnOptions
      .filter((column) => selectedFields.includes(column.field))
      .map((column) => column.title);

    const dateIndexes = headerRow.cells.reduce((indexes, cell, idx) => {
      if (cell.value === "Date" || cell.value === "Received Date") {
        indexes.push(idx);
      }
      return indexes;
    }, []);
    const customsStatusIndex = headerRow.cells.findIndex(
      (x) => x.value === "Customs Status"
    );
    const warehouseIndex = headerRow.cells.findIndex(
      (x) => x.value === "Warehouse"
    );
    const vesselIndex = headerRow.cells.findIndex((x) => x.value === "Vessel");
    const dateIndex = dateIndexes.length > 0 ? dateIndexes[0] : -1;

    const selectedIndices = selectedTitles
      .map((title) => headerRow.cells.findIndex((cell) => cell.value === title))
      .filter((index) => index >= 0);

    const formatRow = (row) => {
      const cloned = { ...row, cells: [...row.cells] };

      if (dateIndexes.length > 0) {
        dateIndexes.forEach((idx) => {
          if (cloned.cells[idx]?.value) {
            cloned.cells[idx].value = formatDate(
              new Date(cloned.cells[idx].value),
              "dd-MMM-yy"
            );
          }
        });
      }

      if (customsStatusIndex >= 0 && cloned.cells[customsStatusIndex]) {
        const statusCode = cloned.cells[customsStatusIndex].value;
        if (statusCode) {
          const cs = CargoCustomsStatus.find((s) => s.code === statusCode);
          if (cs) cloned.cells[customsStatusIndex].value = cs.name;
        }
      }

      const isHazardousRow = cloned.cells.some((cell) => cell.value === "Hazardous");
      if (isHazardousRow) {
        cloned.cells = cloned.cells.map((cell) => ({
          ...cell,
          color: "#ff0000",
        }));
      }

      cloned.cells = selectedIndices.map((index) => {
        const cell = { ...cloned.cells[index] } || { value: "" };
        cell.borderTop = { color: "#000000", size: 1 };
        cell.borderBottom = { color: "#000000", size: 1 };
        cell.borderLeft = { color: "#000000", size: 1 };
        cell.borderRight = { color: "#000000", size: 1 };
        return cell;
      });
      return cloned;
    };

    const originalDataRows = rows.filter((r) => r.type === "data");
    let processedRows = originalDataRows.map(formatRow);

    if ((vesselIndex >= 0 || warehouseIndex >= 0) && originalDataRows.length > 0) {
      // Vessel first, then Warehouse within each vessel, then Received Date
      // oldest-to-newest within each vessel+warehouse combination.
      const sorted = [...originalDataRows].sort((a, b) => {
        if (vesselIndex >= 0) {
          const va = (a.cells[vesselIndex]?.value ?? "").toString();
          const vb = (b.cells[vesselIndex]?.value ?? "").toString();
          const vesselCompare = va.localeCompare(vb, undefined, {
            sensitivity: "base",
          });
          if (vesselCompare !== 0) return vesselCompare;
        }

        if (warehouseIndex >= 0) {
          const wa = (a.cells[warehouseIndex]?.value ?? "").toString();
          const wb = (b.cells[warehouseIndex]?.value ?? "").toString();
          const warehouseCompare = wa.localeCompare(wb, undefined, {
            sensitivity: "base",
          });
          if (warehouseCompare !== 0) return warehouseCompare;
        }

        if (dateIndex >= 0) {
          const da = a.cells[dateIndex]?.value
            ? new Date(a.cells[dateIndex].value).getTime()
            : 0;
          const db = b.cells[dateIndex]?.value
            ? new Date(b.cells[dateIndex].value).getTime()
            : 0;
          return da - db;
        }
        return 0;
      });

      const groupedRows = [];
      let previousVessel = null;
      let previousWarehouse = null;
      sorted.forEach((row, idx) => {
        const vesselName =
          vesselIndex >= 0 ? (row.cells[vesselIndex]?.value ?? "").toString() : null;
        const warehouseName =
          warehouseIndex >= 0
            ? (row.cells[warehouseIndex]?.value ?? "").toString()
            : null;
        if (
          idx > 0 &&
          (vesselName !== previousVessel || warehouseName !== previousWarehouse)
        ) {
          groupedRows.push({
            type: "data",
            cells: selectedIndices.map(() => ({
              value: "",
              borderTop: { color: "#000000", size: 1 },
              borderBottom: { color: "#000000", size: 1 },
              borderLeft: { color: "#000000", size: 1 },
              borderRight: { color: "#000000", size: 1 },
            })),
          });
        }
        groupedRows.push(formatRow(row));
        previousVessel = vesselName;
        previousWarehouse = warehouseName;
      });
      processedRows = groupedRows;
    }

    const filteredHeader = {
      ...headerRow,
      cells: selectedIndices.map((index) => ({ ...headerRow.cells[index] })),
    };

    filteredHeader.cells.forEach((cell) => {
      cell.background = "#4472C4";
      cell.color = "#FFFFFF";
      cell.bold = true;
      cell.textAlign = "center";
      cell.borderTop = { color: "#000000", size: 1 };
      cell.borderBottom = { color: "#000000", size: 1 };
      cell.borderLeft = { color: "#000000", size: 1 };
      cell.borderRight = { color: "#000000", size: 1 };
    });

    sheet.rows = [filteredHeader, ...processedRows];
    exporterRef.current?.save(options);
  };
 
  const calculateAge = (receivedDate) => {
    if (!receivedDate) return ""; // Handle missing date
    const received = new Date(receivedDate);
    const today = new Date();
 
    // Calculate difference in milliseconds and convert to days
    const diffTime = today - received;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
 
    return diffDays;
  };
 
  const loadData = async () => {
    setLoading(true);
    try {
      const customerId = customer == null ? "~ALL" : customer.name;
      const vesselId = vessel == null ? "~ALL" : vessel.name;
      const statusId =
        status == null ? "~ALL" : status.map((s) => s.name).join(",");
      const searchLocation = location === "" ? "~ALL" : location;
      const res = await axios.get(
        `${BASE_URL}/Reports/stock?vessel=${encodeURIComponent(
          vesselId
        )}&customer=${encodeURIComponent(
          customerId
        )}&location=${encodeURIComponent(
          searchLocation
        )}&status=${statusId}&canceledPOs=${canceledPos}`
      );
      console.log(res.data);
 
      if (authCtx.profile.warehouseId) {
        const newRes = res.data.data.filter(
          (x) => x.warehouseId === authCtx.profile.warehouseId
        );
        console.log(newRes);
        
        setData(
          newRes.map((x) => {
            const totalValue = x.totalValue ?? x.TotalValue;
            const currencyCode = x.currencyCode ?? x.CurrencyCode;
            const formattedTotalValue = totalValue != null
              ? Number(totalValue).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              : "";

            return {
              ...x,
              age: calculateAge(x.receivedDate),
              totalValueDisplay: currencyCode
                ? `${currencyCode} ${formattedTotalValue}`
                : formattedTotalValue,
            };
          })
        );
      } else {
        setData(
          res.data.data.map((x) => {
            const totalValue = x.totalValue ?? x.TotalValue;
            const currencyCode = x.currencyCode ?? x.CurrencyCode;
            const formattedTotalValue = totalValue != null
              ? Number(totalValue).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              : "";

            return {
              ...x,
              age: calculateAge(x.receivedDate),
              totalValueDisplay: currencyCode
                ? `${currencyCode} ${formattedTotalValue}`
                : formattedTotalValue,
            };
          })
        );
      }
    } finally {
      setLoading(false);
    }
    // setPageState((pre) => ({
    //   ...pre,
    //   total: res.data.totalCount,
    //   skip: res.data.pageNo,
    //   take: res.data.pageSize,
    // }));
  };
 
  const rowRender = (trElement, props) => {
    const danger = props.dataItem.cargoType === "Hazardous";
    const red = { backgroundColor: "rgb(243, 23, 0, 0.32)" };
    const children = danger
      ? React.Children.map(trElement.props.children, (child) =>
          React.isValidElement(child)
            ? React.cloneElement(child, {
                style: { ...child.props.style, ...red },
              })
            : child
        )
      : trElement.props.children;
    const trProps = { style: danger ? red : trElement.props.style };
    return React.cloneElement(
      trElement,
      { ...trProps },
      children
    );
  };
 
  const search = async () => {
    await loadData();
  };
 
  const reset = () => {
    setData([]);
    //setPageState(defaultState)
    // setGridState(defaultState)
    setCustomer(null);
    setVessel(null);
    setStatus(null);
  };
 
  const onVesselChange = (e) => {
    setVessel(e.value);
  };
 
  const onStatusChange = (e) => {
    setStatus(e.value);
  };
 
  const onCustomerChange = (e) => {
    setCustomer(e.value);
  };
 
  const loadLookups = async () => {
    setLoading(true);
    try {
      let res = await axios.get(`${BASE_URL}/Lookup/Customers?searchText=~ALL`);
      setCustomers(res.data);
 
      res = await axios.get(
        `${BASE_URL}/vessel?searchText=~ALL&page=1&pageSize=1500`
      );
      setVessels(res.data.data);
    } finally {
      setLoading(false);
    }
  };
 
  useEffect(() => {
    loadLookups();
    if (authCtx.profile.role === 300) {
      setLocation(authCtx.profile.warehouse.cityName);
    }
  }, []);
 
  return (
    <div className="mx-4 my-4 sm:mx-6 lg:mx-5">
      <div className="flex mb-4">
        <div className="flex min-w-0 gap-2 items-center">
          <h1 className="text-xl font-bold leading-6 text-gray-900 sm:truncate">
            Stock Report
          </h1>
          <button
            type="button"
            onClick={() => setSchedulerOpen(true)}
            className="h-5 w-5"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="size-6"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5m-9-6h.008v.008H12v-.008ZM12 15h.008v.008H12V15Zm0 2.25h.008v.008H12v-.008ZM9.75 15h.008v.008H9.75V15Zm0 2.25h.008v.008H9.75v-.008ZM7.5 15h.008v.008H7.5V15Zm0 2.25h.008v.008H7.5v-.008Zm6.75-4.5h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V15Zm0 2.25h.008v.008h-.008v-.008Zm2.25-4.5h.008v.008H16.5v-.008Zm0 2.25h.008v.008H16.5V15Z"
              />
            </svg>
          </button>
        </div>
      </div>
 
      <div className="flex flex-col w-full">
        <div className="-my-2 relative sm:-mx-6 lg:-mx-8 w-full">
          <div className="py-2 align-middle overflow-scroll inline-block w-full sm:pl-6 lg:pl-8">
            <div className="flex w-full items-center justify-items-center mb-6">
              <div className="w-1/6 h-8 stockMultiSelect mr-2" id="compo">
                <input
                  name="location"
                  className=" w-full h-full flex py-1 px-3 overflow-x-visible shadow-sm sm:text-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md"
                  placeholder="Enter Location"
                  onChange={(e) => setLocation(e.target.value)}
                  value={location}
                />
              </div>
              <div className="w-1/6 mr-2 h-8" id="compo">
                <ComboBox
                  data={customers}
                  name="customerId"
                  textField="name"
                  className="sm:text-sm focus:ring-0 focus:border-0 py-2 px-3 h-full p-0"
                  placeholder="Enter customer"
                  onChange={onCustomerChange}
                  value={customer}
                  required
                  suggest={true}
                  clearButton={false}
                />
              </div>
              <div className="w-1/6 mr-2 h-8" id="compo">
                <ComboBox
                  data={vessels}
                  name="vesselId"
                  textField="name"
                  className="sm:text-sm focus:ring-0 focus:border-0 py-2 px-3 h-full p-0"
                  placeholder="Enter vessel"
                  onChange={onVesselChange}
                  value={vessel}
                  suggest={true}
                  clearButton={false}
                />
              </div>
              <div className="w-1/6 h-8 stockMultiSelect" id="compo">
                <MultiSelect
                  data={
                    isAgent
                      ? movementStatuses.filter((x) =>
                          agentVisibleMovementStatuses.includes(x.id)
                        )
                      : movementStatuses
                  }
                  name="statusId"
                  textField="name"
                  className=" w-full h-full flex py-1 overflow-x-visible shadow-sm sm:text-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md"
                  placeholder="Enter status"
                  onChange={onStatusChange}
                  value={status}
                  suggest={true}
                  clearButton={false}
                />
              </div>
              <div
                className="ml-3 flex items-center w-1/6 h-8 text-xs text-gray-800"
                id="compo"
              >
                <Checkbox
                  value={canceledPos}
                  checked={canceledPos}
                  onChange={() => setCanceledPos(!canceledPos)}
                />
                Show Canceled POS
              </div>
 
              <div class="flex justify-center mx-4">
                <button
                  className="inline-flex w-full justify-center px-4 items-center h-8 border border-lime-700 bg-lime-700 border-transparent shadow-sm text-sm font-medium rounded-md text-white hover:bg-lime-900 hover:border-lime-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none"
                  onClick={search}
                  disabled={loading}
                >
                  Search
                </button>
                <div className="relative inline-block w-full ml-2">
                  <button
                    title="Export Excel"
                    className="inline-flex w-full justify-center px-4 items-center h-8 border border-blue-700 bg-blue-700 border-transparent shadow-sm text-sm font-medium rounded-md text-white hover:bg-blue-900 hover:border-blue-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none"
                    onClick={() => setExportPickerOpen(!exportPickerOpen)}
                    disabled={loading}
                  >
                    Export
                  </button>
                  {exportPickerOpen && (
                    <div className="absolute right-0 z-50 mt-2 w-72 rounded-md border border-slate-200 bg-white p-3 shadow-lg">
                      <div className="font-semibold text-sm mb-2">Select export columns</div>
                      <div className="max-h-56 overflow-y-auto space-y-1 text-sm">
                        {exportColumnOptions.map((column) => (
                          <label key={column.field} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={selectedExportColumns.includes(column.field)}
                              onChange={() => toggleExportColumn(column.field)}
                              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span>{column.title}</span>
                          </label>
                        ))}
                      </div>
                      <div className="mt-3 flex justify-end gap-2">
                        <button
                          type="button"
                          className="rounded-md border border-slate-300 bg-white px-3 py-1 text-sm text-slate-700 hover:bg-slate-50"
                          onClick={() => setExportPickerOpen(false)}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="rounded-md bg-blue-700 px-3 py-1 text-sm font-medium text-white hover:bg-blue-900"
                          onClick={downloadExport}
                        >
                          Download
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <button
                  className="inline-flex w-full justify-center px-4 ml-2 items-center h-8 border border-orange-700 bg-orange-700 border-transparent shadow-sm text-sm font-medium rounded-md text-white hover:bg-orange-900 hover:border-orange-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none"
                  onClick={reset}
                  disabled={loading}
                >
                  Reset
                </button>
              </div>
            </div>
            <div className="shadow relative w-full overflow-x-auto border-b border-gray-200 sm:rounded-lg">
              {loading && <KendoLoader />}
              <ExcelExport data={data} ref={exporterRef} fileName="StockReport.xlsx">
                {selectedExportColumns.map((field) => {
                  const column = exportColumnOptions.find((item) => item.field === field);
                  const rowField = {
                    PoNo: "poNo",
                    Vessel: "vesselName",
                    Supplier: "supplierName",
                    ShipmentType: "cargoPickupType",
                    TotalValue: "totalValueDisplay",
                    CargoType: "cargoType",
                    Commodity: "commodity",
                    ReceivedDate: "receivedDate",
                    Age: "age",
                    Warehouse: "currentWarehouse",
                    CargoCustomsStatus: "cargoCustomsStatusCode",
                    Ref: "remarks",
                    Qty: "colli",
                    Weight: "weight",
                    Length: "length",
                    Width: "width",
                    Height: "height",
                    ChWeight: "chargableWeight",
                    Volume: "volume",
                    AwbBl: "shipmentRefNo",
                    Status: "movementStatus",
                    Date: "receivedDate",
                  }[field];
                  return (
                    <ExcelExportColumn
                      key={field}
                      field={rowField || field}
                      title={column?.title || field}
                    />
                  );
                })}
              </ExcelExport>
              <Grid
                  data={data}
                  pageable={false}
                  filterable={false}
                  resizable={true}
                  rowRender={rowRender}
                  style={{
                    height: "70vh",
                    overflowY: "scroll",
                    overflowX: "scroll",
                    borderRadius: "10px",
                    borderColor: "#E5E7EB",
                    fontSize: "12px",
                    fontFamily: "poppins",
                    textAlign: "left",
                    width:
                      gridColumns.reduce((acc, column) => {
                        return acc + column.width;
                      }, 20) + "px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {gridColumns.map((c) => (
                    <GridColumn
                      field={c.field}
                      title={c.title}
                      width={c.width}
                      filterable={c.filterable}
                      cell={c?.cell}
                    />
                  ))}
                </Grid>
            </div>
          </div>
        </div>
      </div>
      <ReactModal
        isOpen={schedulerOpen}
        //onAfterOpen={afterOpenModal}
        // onRequestClose={closeModal}
        style={customStyles}
        contentLabel="Example Modal"
        ariaHideApp={false}
      >
        <ScheduleForm
          closeModel={() => setSchedulerOpen(false)}
          customers={customers}
        />
      </ReactModal>
    </div>
  );
}
